import { leadsHandoffScope } from './routeHandoff.js';

// Tab-local navigation state only: never store lead objects, answers or tokens.
const KEY = 'm5m_leads_view_v1';
const LIST_KEY = 'm5m_leads_list_v1';
const RETURN_KEY = 'm5m_leads_connection_return_v1';
const TTL = 30 * 60 * 1000;
let bootRoute = null;
let directVisit = null;
// mvvLegit's startup redirect lands on Home. Restore only these known private
// destinations through the normal router, which still performs its access checks.
export function rememberLeadBootRoute(hash) {
  bootRoute = /^#\/(?:leads|settings\/integrations(?:\/[a-z-]+)?)$/.test(hash)
    ? { path: hash.slice(1), at: Date.now() } : null;
}
export function takeLeadBootRoute() {
  const value = bootRoute; bootRoute = null;
  return value && Date.now() - value.at < TTL ? value.path : null;
}
export const clearLeadBootRoute = () => { bootRoute = null; };
const scopeKey = () => {
  const { actorGuid, orgGuid } = leadsHandoffScope();
  return actorGuid ? JSON.stringify({ actorGuid, orgGuid }) : null;
};
/** Explicit shell navigation starts a list visit, rather than resuming a report journey. */
export function rememberLeadNavigation(route) {
  directVisit = route === '/leads' || route === 'leads'
    ? { scope: scopeKey(), at: Date.now() } : null;
}
export function takeLeadListVisit() {
  const value = directVisit; directVisit = null;
  return !!(value?.scope && value.scope === scopeKey() && Date.now() - value.at < 15000);
}
function read(key) {
  try {
    const scope = scopeKey();
    const value = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (!scope || !value || value.scope !== scope || !Number.isFinite(value.at)
      || value.at > Date.now() || Date.now() - value.at > TTL) {
      sessionStorage.removeItem(key); return null;
    }
    return value;
  } catch { return null; }
}
function write(key, value) {
  const scope = scopeKey();
  if (!scope) return;
  try { sessionStorage.setItem(key, JSON.stringify({ ...value, scope, at: Date.now() })); } catch { /* private/limited storage: memory-only navigation still works */ }
}

function safeView(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const view = {};
  for (const key of ['view','filter','clientFilter','search','day','range','magnetFilter','sourceFilter','from','to','leadId','leadGuid','selectedId']) {
    if (value[key] === null && key === 'selectedId') view[key] = null;
    else if (typeof value[key] === 'string' && value[key].length <= 200) view[key] = value[key];
  }
  for (const key of ['offset','scroll','listScroll']) if (Number.isSafeInteger(value[key]) && value[key] >= 0) view[key] = value[key];
  if (typeof value.drillThrough === 'boolean') view.drillThrough = value.drillThrough;
  if (['captured','name','contact'].includes(value.sort?.key) && ['asc','desc'].includes(value.sort?.dir)) view.sort = { key: value.sort.key, dir: value.sort.dir };
  if (value.returnTo && /^#\/[\w%/-]*$/.test(value.returnTo.hash || '') && !value.returnTo.hash.includes('..')
    && typeof value.returnTo.label === 'string' && value.returnTo.label.length <= 100) {
    view.returnTo = { hash: value.returnTo.hash, label: value.returnTo.label };
  }
  return view;
}
export const readLeadView = () => safeView(read(KEY)?.view);
export const saveLeadView = view => write(KEY, { view: safeView(view) });
// Ordinary list preferences are separate from a temporary report/record journey.
// Never reopen the old selection when the user deliberately chooses Leads.
export const readLeadListView = () => safeView(read(LIST_KEY)?.view);
export const saveLeadListView = view => write(LIST_KEY, { view: safeView({ ...view,
  selectedId: null, leadId: '', leadGuid: '', returnTo: null, drillThrough: false }) });
export const rememberLeadConnectionReturn = () => write(RETURN_KEY, {});

/** A narrow contextual return; existing integration routes own all permissions. */
export function mountLeadConnectionReturn(host) {
  if (!host || host.querySelector('[data-leads-return]') || !read(RETURN_KEY)) return;
  const link = document.createElement('a');
  link.href = '#/leads'; link.className = 'intg-back';
  link.dataset.leadsReturn = ''; link.textContent = '← Back to leads';
  link.addEventListener('click', () => { try { sessionStorage.removeItem(RETURN_KEY); } catch { /* unavailable */ } });
  const wrapper = document.createElement('div'); wrapper.append(link); host.prepend(wrapper);
}
